import { FC, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FileOutlined } from '@ant-design/icons';
import {
  type ModalProps,
  Upload,
  Button,
  Flex,
  Form,
  Modal,
  DatePicker,
  Spin,
  Typography,
} from 'antd';
import { useCurrentCompanyId } from '@hooks';
import { formatFileSize } from '@utils';
import * as S from './styled';

import { useAdminPayslipDocumentController_create  } from '@api-client/generated/AdminPayslipDocumentController/create'

import { Schemas } from '@api-client/generated/types';

type PayslipsModalUploadProps = {
  onCancel: () => void;
  onAfterUpload: () => void;
} & ModalProps;

const PayslipsModalUpload: FC<PayslipsModalUploadProps> = ({
  open,
  onCancel,
  onAfterUpload,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  const [form] = Form.useForm();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { mutate: createDocument, isPending } =
    useAdminPayslipDocumentController_create();

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      const firstDayOfMonth = date.startOf('month');

      form.setFieldsValue({ issueDate: firstDayOfMonth });
    }
  };

  const handleSubmit = (values: Schemas.AdminCreatePayslipDocumentDto) => {
    if (selectedFile && companyId) {
      const formData = new FormData();

      formData.append('file', selectedFile);
      formData.append('issueDate', dayjs(values.issueDate).format());

      createDocument(
        {
          parameter: { companyId },
          requestBody:
            formData as unknown as Schemas.AdminCreatePayslipDocumentDto,
        },
        {
          onSuccess: () => {
            onAfterUpload();
            handleCancel();
          },
        }
      );
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      width={504}
      open={open}
      title="Upload document"
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      centered
      closable={!isPending}
      maskClosable={!isPending}
      keyboard={!isPending}
      {...rest}
    >
      <S.Inner gap={16} vertical>
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
        >
          <Spin spinning={isPending} tip="Uploading…">
            <Flex gap={16} vertical>
              <S.Wrapper>
                <Form.Item
                  label="Issue date"
                  name="issueDate"
                  rules={[
                    { required: true, message: 'Please select an issue date' },
                  ]}
                >
                  <DatePicker
                    picker="month"
                    placeholder="DD.MM.YYYY"
                    format="DD.MM.YYYY"
                    size="large"
                    disabled={isPending}
                    onChange={handleDateChange}
                  />
                </Form.Item>
              </S.Wrapper>

              <Flex gap={16} vertical>
                <Upload
                  customRequest={({ file }) =>
                    file instanceof File && setSelectedFile(file)
                  }
                  beforeUpload={() => {}}
                  showUploadList={false}
                  disabled={isPending}
                >
                  <Button type="primary" size="large" ghost disabled={isPending}>
                    Upload document
                  </Button>
                </Upload>

                {selectedFile && (
                  <S.File align="center" justify="space-between" gap={8}>
                    <Flex align="center" gap={6} style={{ minWidth: 0, flex: 1 }}>
                      <FileOutlined />
                      <Typography.Text
                        ellipsis={{ tooltip: selectedFile.name }}
                      >
                        {selectedFile.name}
                      </Typography.Text>
                    </Flex>
                    {formatFileSize(selectedFile.size)}
                  </S.File>
                )}
              </Flex>

              <Form.Item noStyle>
                <Flex justify="flex-end" gap={12}>
                  <Button
                    onClick={handleCancel}
                    size="large"
                    disabled={isPending}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    disabled={isPending}
                    loading={isPending}
                  >
                    Upload
                  </Button>
                </Flex>
              </Form.Item>
            </Flex>
          </Spin>
        </Form>
      </S.Inner>
    </Modal>
  );
};

export default PayslipsModalUpload;
